import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Slide,
  Link,
  IconButton,
  Collapse,
  FormControlLabel,
  Switch,
  Stack,
  Divider,
} from '@mui/material';
import { Close, Cookie, Settings } from '@mui/icons-material';

const CONSENT_KEY = 'eis_cookie_consent';

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    functional: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if consent has already been given
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allConsent = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(allConsent));
    setVisible(false);
  };

  const handleAcceptSelected = () => {
    const consent = {
      ...preferences,
      essential: true, // Always required
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setVisible(false);
  };

  const handleRejectNonEssential = () => {
    const minimalConsent = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(minimalConsent));
    setVisible(false);
  };

  const handlePreferenceChange = (category) => (event) => {
    if (category === 'essential') return; // Cannot disable essential
    setPreferences((prev) => ({
      ...prev,
      [category]: event.target.checked,
    }));
  };

  if (!visible) return null;

  return (
    <Slide direction="up" in={visible} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          p: 3,
          borderRadius: 0,
          borderTop: '3px solid',
          borderColor: 'primary.main',
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Cookie sx={{ color: 'primary.main', fontSize: 32, mt: 0.5 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                We value your privacy
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                We use cookies to enhance your browsing experience, provide personalized content,
                and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.
                You can customize your preferences or reject non-essential cookies.
                {' '}
                <Link href="/legal" target="_blank" underline="hover">
                  Learn more in our Cookie Policy
                </Link>
              </Typography>

              <Collapse in={showDetails}>
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Cookie Preferences
                  </Typography>
                  <Stack spacing={1}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.essential}
                          disabled
                          size="small"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            Essential Cookies
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Required for the website to function. Cannot be disabled.
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.functional}
                          onChange={handlePreferenceChange('functional')}
                          size="small"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            Functional Cookies
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Remember your preferences (theme, language).
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.analytics}
                          onChange={handlePreferenceChange('analytics')}
                          size="small"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            Analytics Cookies
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Help us understand how visitors use our site.
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.marketing}
                          onChange={handlePreferenceChange('marketing')}
                          size="small"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            Marketing Cookies
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Used to track visitors across websites for advertising.
                          </Typography>
                        </Box>
                      }
                    />
                  </Stack>
                </Box>
              </Collapse>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleAcceptAll}
                  sx={{ minWidth: 120 }}
                >
                  Accept All
                </Button>
                {showDetails ? (
                  <Button
                    variant="outlined"
                    onClick={handleAcceptSelected}
                    sx={{ minWidth: 120 }}
                  >
                    Save Preferences
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    onClick={() => setShowDetails(true)}
                    startIcon={<Settings />}
                    sx={{ minWidth: 120 }}
                  >
                    Customize
                  </Button>
                )}
                <Button
                  variant="text"
                  onClick={handleRejectNonEssential}
                  color="inherit"
                  sx={{ minWidth: 120 }}
                >
                  Reject Non-Essential
                </Button>
              </Stack>
            </Box>
            <IconButton
              onClick={handleRejectNonEssential}
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <Close />
            </IconButton>
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
}

/**
 * Hook to check if a cookie category is consented
 * Usage: const canUseAnalytics = useCookieConsent('analytics');
 */
export function useCookieConsent(category) {
  const [consented, setConsented] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (consent) {
      try {
        const parsed = JSON.parse(consent);
        setConsented(!!parsed[category]);
      } catch {
        setConsented(false);
      }
    }
  }, [category]);

  return consented;
}

/**
 * Function to reset cookie consent (useful for settings page)
 */
export function resetCookieConsent() {
  localStorage.removeItem(CONSENT_KEY);
  window.location.reload();
}
